// Matches a parsed unit report against the existing `units` collection and
// writes the result: new units created, recharter/Key-3 state overwritten on
// the unit doc, advancement written as append-only snapshots — plus a
// `unitImports` record for the "last imported" line.

import { doc, getDocs, limit, orderBy, query, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { collections } from './db';
import { resolveActiveTenantId, tenantWhere } from './tenant';
import type {
  AdvancementRow,
  CharterOrgRow,
  CommissionerAssignmentRow,
  Key3Row,
  ParsedConnectionsReport,
  ParsedLeaderReport,
  ParsedReport,
  ParsedRow,
  ParsedSytReport,
  RechartRow,
  TrainedLeaderRow,
} from './unitReports';
import type {
  ISODateString,
  LeaderPositionEntry,
  Unit,
  UnitImport,
  UnitKey3Snapshot,
  UnitLeader,
  UnitRecharterSnapshot,
  UnitReportType,
  UnitSyt,
  UnitSytSnapshot,
} from '../types';

export interface ImportPreview {
  reportType: UnitReportType;
  reportDate: string;
  matched: number;
  newUnits: number;
  unmatched: string[];
}

async function loadUnits(): Promise<Unit[]> {
  // Tenant-scoped (Phase 5B) — an import must only ever match against, or
  // create duplicates within, its OWN tenant's units. Without this filter
  // an import run under one tenant could silently match/patch another
  // tenant's unit by name/scoutbookUnitId collision.
  const snap = await getDocs(query(collections.units(), tenantWhere()));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Unit, 'id'>) }));
}

function indexUnits(units: Unit[]) {
  const byScoutbook = new Map<string, Unit>();
  const byName = new Map<string, Unit>(); // `${districtId}|||${normalizedName}`
  for (const u of units) {
    if (u.scoutbookUnitId) byScoutbook.set(u.scoutbookUnitId, u);
    byName.set(`${u.districtId}|||${u.normalizedName}`, u);
  }
  return { byScoutbook, byName };
}

function matchUnit(
  row: ParsedRow,
  idx: ReturnType<typeof indexUnits>,
): Unit | undefined {
  if (row.scoutbookUnitId && idx.byScoutbook.has(row.scoutbookUnitId)) {
    return idx.byScoutbook.get(row.scoutbookUnitId);
  }
  return idx.byName.get(`${row.districtId}|||${row.normalizedName}`);
}

/** Chunked batch commit — Firestore caps a batch at 500 writes. */
async function commit(ops: ((b: ReturnType<typeof writeBatch>) => void)[]) {
  for (let i = 0; i < ops.length; i += 400) {
    const batch = writeBatch(db);
    for (const op of ops.slice(i, i + 400)) op(batch);
    await batch.commit();
  }
}

export async function importUnitReport(
  report:
    | ParsedReport<RechartRow>
    | ParsedReport<CharterOrgRow>
    | ParsedReport<Key3Row>
    | ParsedReport<AdvancementRow>
    | ParsedReport<CommissionerAssignmentRow>,
  importedBy: string,
  fileName: string,
): Promise<ImportPreview> {
  const existing = await loadUnits();
  const idx = indexUnits(existing);
  const now = serverTimestamp();
  const importRef = doc(collections.unitImports());
  const ops: ((b: ReturnType<typeof writeBatch>) => void)[] = [];

  let matched = 0;
  let newUnits = 0;

  // Track units created in THIS import so a second row for the same unit
  // (e.g. Key 3 quirks) reuses it rather than making a duplicate.
  const createdThisRun = new Map<string, string>(); // key -> new doc id

  const resolveUnitId = (row: ParsedRow): string => {
    const hit = matchUnit(row, idx);
    if (hit) {
      matched++;
      // Backfill the my.Scouting id / charter org if this report has them.
      const patch: Partial<Unit> = {};
      if (row.scoutbookUnitId && !hit.scoutbookUnitId) patch.scoutbookUnitId = row.scoutbookUnitId;
      if (row.charterOrg && !hit.charterOrg) patch.charterOrg = row.charterOrg;
      if (Object.keys(patch).length) {
        ops.push((b) => b.update(doc(collections.units(), hit.id), { ...patch, updatedAt: now }));
      }
      return hit.id;
    }
    const key = `${row.districtId}|||${row.normalizedName}`;
    const already = createdThisRun.get(key);
    if (already) return already;
    newUnits++;
    const ref = doc(collections.units());
    createdThisRun.set(key, ref.id);
    ops.push((b) =>
      b.set(ref, {
        districtId: row.districtId,
        scoutbookUnitId: row.scoutbookUnitId,
        name: row.name,
        normalizedName: row.normalizedName,
        type: row.type,
        charterOrg: row.charterOrg,
        tenantId: resolveActiveTenantId(),
        createdAt: now,
        updatedAt: now,
      }),
    );
    return ref.id;
  };

  if (report.reportType === 'recharter') {
    const rechartRows = (report as ParsedReport<RechartRow>).rows;
    const tenantId = resolveActiveTenantId();
    for (const row of rechartRows) {
      const unitId = resolveUnitId(row);
      ops.push((b) =>
        b.set(
          doc(collections.units(), unitId),
          {
            recharter: {
              charterExpires: row.charterExpires,
              reportedExpires: row.reportedExpires,
              nextCharterExpires: row.nextCharterExpires,
              paidYouth: row.paidYouth,
              paidAdults: row.paidAdults,
              scoutLifeYouth: row.scoutLifeYouth,
              scoutLifeAdults: row.scoutLifeAdults,
              reportDate: report.reportDate,
              importedAt: now,
            },
            updatedAt: now,
          },
          { merge: true },
        ),
      );

      // Append-only PER-UNIT history (see UnitRecharterSnapshot in
      // ../types) — additive to, never a replacement for, the current-
      // state Unit.recharter write above or the district-level
      // membershipSnapshots aggregate below. Deterministic doc id makes a
      // same-date re-import overwrite this exact record rather than
      // duplicating it.
      const recharterSnap: Omit<UnitRecharterSnapshot, 'id'> = {
        tenantId,
        unitId,
        districtId: row.districtId,
        reportDate: report.reportDate,
        importId: importRef.id,
        importedAt: now as never,
        // Raw, as the report states it; the renewed date (if any) is kept beside it.
        charterExpires: row.reportedExpires,
        nextCharterExpires: row.nextCharterExpires,
        paidYouth: row.paidYouth,
        paidAdults: row.paidAdults,
        scoutLifeYouth: row.scoutLifeYouth,
        scoutLifeAdults: row.scoutLifeAdults,
      };
      ops.push((b) =>
        b.set(
          doc(collections.unitRecharterSnapshots(), buildSnapshotId(tenantId, report.reportDate, unitId)),
          recharterSnap,
        ),
      );
    }

    // Snapshot this month's membership per district from the recharter roll —
    // one doc per district, overwritten if the month is re-imported. Feeds the
    // dashboard trend line (see src/lib/membership.ts).
    const month = report.reportDate.slice(0, 7); // 'YYYY-MM'
    const byDistrict = new Map<string, { youth: number; adults: number; units: number }>();
    for (const row of rechartRows) {
      const acc = byDistrict.get(row.districtId) ?? { youth: 0, adults: 0, units: 0 };
      acc.youth += row.paidYouth || 0;
      acc.adults += row.paidAdults || 0;
      acc.units += 1;
      byDistrict.set(row.districtId, acc);
    }
    for (const [districtId, acc] of byDistrict) {
      ops.push((b) =>
        b.set(doc(collections.membershipSnapshots(), `${districtId}__${month}`), {
          districtId,
          month,
          youth: acc.youth,
          adults: acc.adults,
          units: acc.units,
          source: 'recharterImport',
          reportDate: report.reportDate,
          tenantId: resolveActiveTenantId(),
          updatedAt: now,
        }),
      );
    }
  } else if (report.reportType === 'charterOrg') {
    for (const row of (report as ParsedReport<CharterOrgRow>).rows) {
      const unitId = resolveUnitId(row);
      ops.push((b) =>
        b.set(
          doc(collections.units(), unitId),
          {
            charterOrgDetail: {
              orgTypeCode: row.orgTypeCode,
              orgType: row.orgType,
              orgTypeShort: row.orgTypeShort,
              address: row.address,
              cityState: row.cityState,
              zip: row.zip,
              phone: row.phone,
              tenureMonths: row.tenureMonths,
              genderAccepted: row.genderAccepted,
              expiryDate: row.expiryDate,
              paidYouth: row.paidYouth,
              paidAdults: row.paidAdults,
              specialInterestCode: row.specialInterestCode,
              specialInterestType: row.specialInterestType,
              cisCouncilPaid: row.cisCouncilPaid,
              reportDate: report.reportDate,
              importedAt: now,
            },
            updatedAt: now,
          },
          { merge: true },
        ),
      );
    }
  } else if (report.reportType === 'key3') {
    const tenantId = resolveActiveTenantId();
    for (const row of (report as ParsedReport<Key3Row>).rows) {
      const unitId = resolveUnitId(row);
      ops.push((b) =>
        b.set(
          doc(collections.units(), unitId),
          {
            key3: {
              hasCommitteeChair: row.hasCommitteeChair,
              hasUnitLeader: row.hasUnitLeader,
              hasCharterOrgRep: row.hasCharterOrgRep,
              worstYpt: row.worstYpt,
              people: row.people,
              reportDate: report.reportDate,
              importedAt: now,
            },
            updatedAt: now,
          },
          { merge: true },
        ),
      );

      // Append-only PER-UNIT history (see UnitKey3Snapshot in ../types) —
      // additive to, never a replacement for, the current-state Unit.key3
      // write above. Same deterministic-id idempotency pattern as the
      // recharter snapshot above.
      const key3Snap: Omit<UnitKey3Snapshot, 'id'> = {
        tenantId,
        unitId,
        districtId: row.districtId,
        reportDate: report.reportDate,
        importId: importRef.id,
        importedAt: now as never,
        hasCommitteeChair: row.hasCommitteeChair,
        hasUnitLeader: row.hasUnitLeader,
        hasCharterOrgRep: row.hasCharterOrgRep,
        worstYpt: row.worstYpt,
        people: row.people,
      };
      ops.push((b) =>
        b.set(doc(collections.unitKey3Snapshots(), buildSnapshotId(tenantId, report.reportDate, unitId)), key3Snap),
      );
    }
  } else if (report.reportType === 'commissionerAssignment') {
    for (const row of (report as ParsedReport<CommissionerAssignmentRow>).rows) {
      const unitId = resolveUnitId(row);
      ops.push((b) =>
        b.set(
          doc(collections.units(), unitId),
          {
            // Current-state, from this report directly — never touches the
            // manual commissionerContactId assignment on the Commissioners
            // page; that page compares this suggestion against it by name.
            commissionerActivity: {
              metricsMet: row.metricsMet,
              lastConnectionDate: row.lastConnectionDate,
              lastAssessmentDate: row.lastAssessmentDate,
              lastAssessmentScore: row.lastAssessmentScore,
              reportDate: report.reportDate,
              importedAt: now,
            },
            commissionerImportSuggestion: {
              name: row.assignedName,
              memberId: row.assignedMemberId,
              registrationExpired: row.registrationExpired,
              unitExpired: row.unitExpired,
              unitExpiredDate: row.unitExpiredDate,
              reportDate: report.reportDate,
              importedAt: now,
            },
            updatedAt: now,
          },
          { merge: true },
        ),
      );
    }
  } else {
    const program = report.reportType === 'advancementCub' ? 'cub' : 'scoutsBSA';
    const programYear = report.programYear ?? String(new Date().getFullYear());
    for (const row of (report as ParsedReport<AdvancementRow>).rows) {
      const unitId = resolveUnitId(row);
      const snapRef = doc(collections.unitAdvancementSnapshots());
      ops.push((b) =>
        b.set(snapRef, {
          unitId,
          districtId: row.districtId,
          program,
          programYear,
          importId: importRef.id,
          reportDate: report.reportDate,
          importedAt: now,
          youth: row.youth,
          ranksYtd: row.ranksYtd,
          eaglesYtd: row.eaglesYtd,
          meritBadgesYtd: row.meritBadgesYtd,
          byRank: row.byRank,
          tenantId: resolveActiveTenantId(),
        }),
      );

      // Denormalize the current state onto the unit doc — only if this report
      // is at least as recent as what's there (guards re-importing an older
      // report). New units have nothing there yet.
      const prior = matchUnit(row, idx)?.latestAdvancement?.[program]?.reportDate;
      if (!prior || report.reportDate >= prior) {
        ops.push((b) =>
          b.set(
            doc(collections.units(), unitId),
            {
              latestAdvancement: {
                [program]: {
                  reportDate: report.reportDate,
                  programYear,
                  youth: row.youth,
                  ranksYtd: row.ranksYtd,
                  eaglesYtd: row.eaglesYtd,
                  meritBadgesYtd: row.meritBadgesYtd,
                  byRank: row.byRank,
                  importedAt: now,
                },
              },
              updatedAt: now,
            },
            { merge: true },
          ),
        );
      }
    }
  }

  ops.push((b) =>
    b.set(importRef, {
      reportType: report.reportType,
      reportDate: report.reportDate,
      importedAt: now,
      importedBy,
      rowCount: report.rows.length,
      matchedCount: matched,
      newUnitCount: newUnits,
      unmatchedCount: report.unmatched.length,
      tenantId: resolveActiveTenantId(),
      fileName,
    }),
  );

  await commit(ops);

  return {
    reportType: report.reportType,
    reportDate: report.reportDate,
    matched,
    newUnits,
    unmatched: report.unmatched,
  };
}

/** BSA Connections History — a growing visit log, not a snapshot: unlike
 *  every other import here, a re-import must ADD to what's there, never
 *  replace it. Deduped by the report's own Connection_ID used as the doc
 *  id, so re-importing an overlapping date range safely upserts instead of
 *  duplicating — no separate existence check needed. Also refreshes each
 *  touched unit's current metric count (identical figure to the
 *  Assigned/Unassigned report), preserving whatever connection/assessment
 *  dates are already on the unit since this report doesn't carry them. */
export async function importConnectionsHistory(
  report: ParsedConnectionsReport,
  importedBy: string,
  fileName: string,
): Promise<ImportPreview> {
  const existing = await loadUnits();
  const idx = indexUnits(existing);
  const now = serverTimestamp();
  const ops: ((b: ReturnType<typeof writeBatch>) => void)[] = [];

  let matched = 0;
  const unmatchedUnits: string[] = [];
  const metricsRefreshed = new Set<string>();

  for (const row of report.rows) {
    const hit =
      (row.scoutbookUnitId && idx.byScoutbook.get(row.scoutbookUnitId)) ||
      idx.byName.get(`${row.districtId}|||${row.normalizedUnitName}`);
    if (!hit) {
      unmatchedUnits.push(row.unitDesignation);
      continue;
    }
    matched++;

    if (!metricsRefreshed.has(hit.id)) {
      metricsRefreshed.add(hit.id);
      ops.push((b) =>
        b.set(
          doc(collections.units(), hit.id),
          {
            commissionerActivity: {
              metricsMet: row.metricsMet,
              lastConnectionDate: hit.commissionerActivity?.lastConnectionDate ?? null,
              lastAssessmentDate: hit.commissionerActivity?.lastAssessmentDate ?? null,
              lastAssessmentScore: hit.commissionerActivity?.lastAssessmentScore ?? null,
              reportDate: report.reportDate,
              importedAt: now,
            },
            updatedAt: now,
          },
          { merge: true },
        ),
      );
    }

    if (!row.connectionId) continue; // no real visit logged for this row
    const connectionId = row.connectionId;
    ops.push((b) =>
      b.set(doc(collections.unitConnections(), connectionId), {
        unitId: hit.id,
        districtId: row.districtId,
        connectionDate: row.connectionDate,
        completeDate: row.completeDate,
        status: row.status,
        loggedByName: row.loggedByName,
        loggedByMemberId: row.loggedByMemberId,
        metricsMet: row.metricsMet,
        comments: row.comments,
        reportDate: report.reportDate,
        importedAt: now,
        tenantId: resolveActiveTenantId(),
      }),
    );
  }

  ops.push((b) =>
    b.set(doc(collections.unitImports()), {
      reportType: 'connectionsHistory',
      reportDate: report.reportDate,
      importedAt: now,
      importedBy,
      rowCount: report.rows.length,
      matchedCount: matched,
      newUnitCount: 0,
      tenantId: resolveActiveTenantId(),
      unmatchedCount: report.unmatched.length + unmatchedUnits.length,
      fileName,
    }),
  );

  await commit(ops);

  return {
    reportType: 'connectionsHistory',
    reportDate: report.reportDate,
    matched,
    newUnits: 0,
    unmatched: [...report.unmatched, ...unmatchedUnits],
  };
}

// ---- Leadership snapshot trust safeguards -------------------------------
// A Trained Leaders import is a COMPLETE council roster each time, which is
// exactly what makes wipe+rewrite safe for unitLeaders below — but it's
// also what makes a partial/mis-scoped/truncated file dangerous to treat as
// HISTORY: an incomplete export looks identical, in shape, to "everyone in
// that scope really left." These checks gate ONLY the historical
// unitLeaderSnapshots write inside importTrainedLeaders() — a failed check
// never rejects or alters the current-state unitLeaders wipe+rewrite, which
// behaves exactly as it always has. See docs/product-module-
// architecture.md's Leadership History section for the full rationale.
//
// Thresholds were chosen from Blackhawk's actual recent import history
// (2026-09-05 through 2026-09-12: row counts 2586, 2587, 2587, 2613 — real
// week-to-week deltas well under 2%), not picked arbitrarily:
//   - SNAPSHOT_MIN_ROW_COUNT: a real council-wide export has thousands of
//     rows; a file with fewer than 10 is never a legitimate whole-council
//     Trained Leaders report, no matter what came before it.
//   - SNAPSHOT_ROW_DROP_THRESHOLD: real week-to-week variation is under 2%;
//     a mis-scoped export (e.g. one district instead of the whole council)
//     would drop the row count by 50%+ given Blackhawk's several districts.
//     20% sits comfortably above normal noise and well below a scoping
//     failure's real signature.
// No historical per-district data existed before this feature (unitLeaders
// was always wipe-and-replace with zero retention), so the per-district
// coverage check below is deliberately conservative rather than tuned to
// an observed distribution: it only trips when a district goes from a real
// presence to a hard zero, never on an ordinary count change.
export const SNAPSHOT_MIN_ROW_COUNT = 10;
export const SNAPSHOT_ROW_DROP_THRESHOLD = 0.8;

export interface SnapshotTrustResult {
  trusted: boolean;
  reason: string | null;
}

/** Basic sanity check, not full calendar validation — rejects an
 *  unparseable date or one implausibly far in the future (a stale/garbled
 *  report header). A PAST date is always accepted: an intentional
 *  historical backfill is a legitimate use of this import. */
export function isCredibleReportDate(reportDate: ISODateString): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(reportDate)) return false;
  const d = new Date(`${reportDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  const oneDayMs = 24 * 60 * 60 * 1000;
  return d.getTime() <= Date.now() + oneDayMs;
}

/** Pure — no Firestore, so it's cheap to test/copy in isolation (see
 *  scripts/test-leadership-snapshots.ts). `previousRowCount` is null when
 *  there's no prior successful snapshot to compare against (first-ever
 *  import). `previousDistrictCounts`/`currentDistrictCounts` map
 *  districtId -> number of unit/district-scoped leader records, used only
 *  to catch a district silently disappearing from an otherwise
 *  plausible-looking file. */
export function evaluateSnapshotTrust(
  reportDate: ISODateString,
  currentRowCount: number,
  previousRowCount: number | null,
  currentDistrictCounts: Map<string, number>,
  previousDistrictCounts: Map<string, number>,
): SnapshotTrustResult {
  if (!isCredibleReportDate(reportDate)) {
    return { trusted: false, reason: `report date "${reportDate}" is not a credible date` };
  }
  if (currentRowCount < SNAPSHOT_MIN_ROW_COUNT) {
    return {
      trusted: false,
      reason: `row count ${currentRowCount} is below the credible floor of ${SNAPSHOT_MIN_ROW_COUNT} for a council-wide export`,
    };
  }
  if (previousRowCount !== null && currentRowCount < previousRowCount * SNAPSHOT_ROW_DROP_THRESHOLD) {
    const pct = Math.round((1 - currentRowCount / previousRowCount) * 100);
    return {
      trusted: false,
      reason: `row count dropped ${pct}% versus the prior successful import (${previousRowCount} -> ${currentRowCount}) — looks like a partial or mis-scoped export`,
    };
  }
  for (const [districtId, priorCount] of previousDistrictCounts) {
    if (priorCount > 0 && !currentDistrictCounts.get(districtId)) {
      return {
        trusted: false,
        reason: `district ${districtId} had ${priorCount} leader record(s) in the prior import and has none in this one`,
      };
    }
  }
  return { trusted: true, reason: null };
}

/** Deterministic doc id for one leadership-snapshot observation — reuses
 *  importTrainedLeaders()'s own grouping key (memberId/scope/districtId/
 *  unit) prefixed with tenant + snapshot date, matching the
 *  `${tenantId}__...` id pattern already used for tenantMemberships/
 *  districtSummaries. The tenant prefix is a real safety measure, not just
 *  convention: memberId is a BSA National id, not tenant-scoped, so two
 *  different tenants CAN genuinely share one. */
export function buildSnapshotId(tenantId: string, snapshotDate: ISODateString, groupKey: string): string {
  return `${tenantId}__${snapshotDate}__${groupKey}`;
}

/** The Trained Leaders report is a COMPLETE council roster each time, so the
 *  `unitLeaders` collection is wiped and rewritten whole — that way someone
 *  who dropped a position doesn't linger. One doc per (person, scope,
 *  district, unit). Also writes an append-only unitLeaderSnapshots record
 *  of this same roster for history (see UnitLeaderSnapshot in ../types),
 *  UNLESS evaluateSnapshotTrust() rejects the import as an unreliable basis
 *  for history — the current-state unitLeaders write above is unaffected
 *  either way. */
export async function importTrainedLeaders(
  report: ParsedLeaderReport,
  importedBy: string,
  fileName: string,
): Promise<ImportPreview> {
  const tenantId = resolveActiveTenantId();
  const existingUnits = await loadUnits();
  const unitByName = new Map<string, Unit>();
  for (const u of existingUnits) unitByName.set(`${u.districtId}|||${u.normalizedName}`, u);

  // group person-position rows into one record per scope location
  const groups = new Map<string, TrainedLeaderRow[]>();
  for (const row of report.rows) {
    const unitKey = row.normalizedUnitName ?? '';
    const key = `${row.memberId}__${row.scope}__${row.districtId ?? ''}__${unitKey}`;
    const arr = groups.get(key) ?? [];
    arr.push(row);
    groups.set(key, arr);
  }

  const now = serverTimestamp();
  const importRef = doc(collections.unitImports());
  const ops: ((b: ReturnType<typeof writeBatch>) => void)[] = [];

  // wipe — SCOPED to this tenant only (Phase 5B). Trained Leaders is a
  // complete-roster-each-time report, but "complete" means complete for
  // THIS tenant's council, never every tenant's data in the collection —
  // an unscoped wipe here would delete every other tenant's unitLeaders
  // the moment a second tenant existed.
  const existing = await getDocs(query(collections.unitLeaders(), tenantWhere()));
  for (const d of existing.docs) ops.push((b) => b.delete(d.ref));

  // Trust-safeguard inputs, gathered before we touch anything: the prior
  // roster's per-district counts (from the docs we're about to delete
  // above — read, not yet deleted) and the prior successful snapshot's row
  // count (from unitImports — small collection, cheap to query; a same-
  // date correction's own about-to-be-replaced entry is excluded by
  // picking the first result with an earlier reportDate, not just the most
  // recent by insertion).
  const previousDistrictCounts = new Map<string, number>();
  for (const d of existing.docs) {
    const districtId = (d.data() as UnitLeader).districtId;
    if (districtId) previousDistrictCounts.set(districtId, (previousDistrictCounts.get(districtId) ?? 0) + 1);
  }
  const priorImportsSnap = await getDocs(
    query(
      collections.unitImports(),
      tenantWhere(),
      where('reportType', '==', 'trainedLeaders'),
      orderBy('reportDate', 'desc'),
      limit(5),
    ),
  );
  const priorImport = priorImportsSnap.docs
    .map((d) => d.data() as UnitImport)
    .find((i) => i.reportDate < report.reportDate && i.snapshotCreated);
  const previousRowCount = priorImport?.snapshotRowCount ?? null;

  let unitMatched = 0;
  let unmatchedUnits = 0;
  const currentDistrictCounts = new Map<string, number>();

  const snapshotRows: {
    groupKey: string;
    first: TrainedLeaderRow;
    unitId: string | null;
    positions: LeaderPositionEntry[];
    registrationExpires: string | null;
  }[] = [];

  for (const [groupKey, rows] of groups.entries()) {
    const first = rows[0];
    let unitId: string | null = null;
    if (first.scope === 'unit' && first.districtId && first.normalizedUnitName) {
      const hit = unitByName.get(`${first.districtId}|||${first.normalizedUnitName}`);
      if (hit) {
        unitId = hit.id;
        unitMatched++;
      } else {
        unmatchedUnits++;
      }
    }
    if (first.districtId) {
      currentDistrictCounts.set(first.districtId, (currentDistrictCounts.get(first.districtId) ?? 0) + 1);
    }
    const positions: LeaderPositionEntry[] = rows.map((r) => ({
      position: r.position,
      trained: r.trained,
      directContact: r.directContact,
      incompleteCourses: r.incompleteCourses,
    }));
    const expiries = rows
      .map((r) => r.registrationExpires)
      .filter((d): d is string => !!d)
      .sort();
    const leader: Omit<UnitLeader, 'id'> = {
      memberId: first.memberId,
      firstName: first.firstName,
      lastName: first.lastName,
      fullName: first.fullName,
      email: first.email,
      zip: first.zip,
      program: first.program,
      scope: first.scope,
      districtId: first.districtId,
      unitId,
      unitName: first.unitDesignation,
      charterOrg: first.charterOrg,
      positions,
      registrationExpires: expiries[0] ?? null,
      anyUntrained: positions.some((p) => !p.trained),
      reportDate: report.reportDate,
      importedAt: now as never,
      tenantId,
    };
    ops.push((b) => b.set(doc(collections.unitLeaders()), leader));
    snapshotRows.push({ groupKey, first, unitId, positions, registrationExpires: expiries[0] ?? null });
  }

  const trust = evaluateSnapshotTrust(
    report.reportDate,
    report.rows.length,
    previousRowCount,
    currentDistrictCounts,
    previousDistrictCounts,
  );

  if (trust.trusted) {
    // Same-date replacement: delete whatever snapshot docs already exist
    // for this exact date first, so a corrected re-import — including one
    // with FEWER rows than the version it replaces — ends up with exactly
    // one canonical set of docs for the date, never the union of old and
    // new. Deterministic ids alone would leave a shrunk correction's
    // dropped records behind; this delete step is what actually prevents
    // that.
    const existingSnaps = await getDocs(
      query(
        collections.unitLeaderSnapshots(),
        tenantWhere(),
        where('snapshotDate', '==', report.reportDate),
      ),
    );
    for (const d of existingSnaps.docs) ops.push((b) => b.delete(d.ref));

    for (const { groupKey, first, unitId, positions, registrationExpires } of snapshotRows) {
      const snapshotId = buildSnapshotId(tenantId, report.reportDate, groupKey);
      const snapshot = {
        tenantId,
        snapshotDate: report.reportDate,
        importId: importRef.id,
        memberId: first.memberId,
        firstName: first.firstName,
        lastName: first.lastName,
        scope: first.scope,
        districtId: first.districtId,
        unitId,
        unitName: first.unitDesignation,
        positions: positions.map((p) => p.position),
        trainedPositions: positions.filter((p) => p.trained).map((p) => p.position),
        anyUntrained: positions.some((p) => !p.trained),
        registrationExpires,
        importedAt: now,
      };
      ops.push((b) => b.set(doc(collections.unitLeaderSnapshots(), snapshotId), snapshot));
    }
  }

  ops.push((b) =>
    b.set(importRef, {
      reportType: 'trainedLeaders',
      reportDate: report.reportDate,
      importedAt: now,
      importedBy,
      rowCount: report.rows.length,
      matchedCount: unitMatched,
      newUnitCount: 0,
      tenantId,
      unmatchedCount: report.unmatched.length + unmatchedUnits,
      unmatchedParseCount: report.unmatched.length,
      unmatchedUnitLinkCount: unmatchedUnits,
      snapshotCreated: trust.trusted,
      snapshotSkippedReason: trust.reason,
      snapshotRowCount: trust.trusted ? report.rows.length : null,
      fileName,
    }),
  );

  await commit(ops);

  return {
    reportType: 'trainedLeaders',
    reportDate: report.reportDate,
    matched: groups.size,
    newUnits: 0,
    unmatched: [
      ...report.unmatched,
      ...(unmatchedUnits ? [`${unmatchedUnits} unit-scoped leaders not linked to a Compass unit`] : []),
    ],
  };
}

/** The SYT Aging Report is the whole council each time — wipe + rewrite the
 *  `unitSyt` collection. One doc per person (by member id), joined to
 *  `unitLeaders` in the UI. */
export async function importSyt(
  report: ParsedSytReport,
  importedBy: string,
  fileName: string,
): Promise<ImportPreview> {
  const now = serverTimestamp();
  const tenantId = resolveActiveTenantId();
  const importRef = doc(collections.unitImports());
  const ops: ((b: ReturnType<typeof writeBatch>) => void)[] = [];

  // wipe — SCOPED to this tenant only (Phase 5B), same reasoning as
  // importTrainedLeaders' wipe above.
  const existing = await getDocs(query(collections.unitSyt(), tenantWhere()));
  for (const d of existing.docs) ops.push((b) => b.delete(d.ref));

  // Read-only, for the per-unit SYT snapshot below ONLY — SYT itself
  // (UnitSyt above) has no unitId at all, so resolving "which unit is
  // this person's SYT status about" means joining against whichever
  // unitLeaders roster is CURRENT right now. See UnitSytSnapshot's own
  // doc comment (../types) for why this is an approximation (SYT and
  // Trained Leaders are imported independently, with no shared as-of
  // date) and why a member with no current unit-scoped position gets no
  // snapshot row at all. A member holding unit-scoped positions at more
  // than one unit gets one row per unit.
  const currentUnitLeaders = await getDocs(query(collections.unitLeaders(), tenantWhere()));
  const unitMatchesByMember = new Map<string, { unitId: string; districtId: string | null }[]>();
  for (const d of currentUnitLeaders.docs) {
    const l = d.data() as UnitLeader;
    if (l.scope !== 'unit' || !l.unitId) continue;
    const arr = unitMatchesByMember.get(l.memberId) ?? [];
    if (!arr.some((m) => m.unitId === l.unitId)) arr.push({ unitId: l.unitId, districtId: l.districtId });
    unitMatchesByMember.set(l.memberId, arr);
  }

  for (const row of report.rows) {
    const rec: Omit<UnitSyt, 'id'> = {
      memberId: row.memberId,
      firstName: row.firstName,
      lastName: row.lastName,
      fullName: row.fullName,
      email: row.email,
      phone: row.phone,
      isCurrent: row.isCurrent,
      expiresOn: row.expiresOn,
      completedOn: row.completedOn,
      modulesToDo: row.modulesToDo,
      districtIds: row.districtIds,
      reportDate: report.reportDate,
      importedAt: now as never,
      tenantId,
    };
    ops.push((b) => b.set(doc(collections.unitSyt()), rec));

    // Append-only PER-UNIT history (see UnitSytSnapshot in ../types) —
    // additive to, never a replacement for, the current-state UnitSyt
    // wipe+rewrite above. Deterministic doc id (unit + member + date)
    // makes a same-date re-import overwrite these exact records rather
    // than duplicating them.
    for (const match of unitMatchesByMember.get(row.memberId) ?? []) {
      const sytSnap: Omit<UnitSytSnapshot, 'id'> = {
        tenantId,
        unitId: match.unitId,
        districtId: match.districtId,
        reportDate: report.reportDate,
        importId: importRef.id,
        importedAt: now as never,
        memberId: row.memberId,
        fullName: row.fullName,
        isCurrent: row.isCurrent,
        expiresOn: row.expiresOn,
        completedOn: row.completedOn,
      };
      ops.push((b) =>
        b.set(
          doc(
            collections.unitSytSnapshots(),
            buildSnapshotId(tenantId, report.reportDate, `${match.unitId}__${row.memberId}`),
          ),
          sytSnap,
        ),
      );
    }
  }

  ops.push((b) =>
    b.set(importRef, {
      reportType: 'syt',
      reportDate: report.reportDate,
      importedAt: now,
      importedBy,
      rowCount: report.rows.length,
      matchedCount: report.rows.length,
      newUnitCount: 0,
      tenantId,
      unmatchedCount: report.unmatched.length,
      fileName,
    }),
  );

  await commit(ops);

  return {
    reportType: 'syt',
    reportDate: report.reportDate,
    matched: report.rows.length,
    newUnits: 0,
    unmatched: report.unmatched,
  };
}
